import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";

import { formatPercent } from "@/features/matches";

import type { DrillOption, DrillSpec } from "../../model/Drill";
import { formatNumber } from "../../model/formatNumber";

export type OptionNoteTone = "good" | "muted" | "bad";
export type OptionNoteView = { text: string; tone: OptionNoteTone };

const CHASE = msg({
  message:
    "{kind, select, dash {dash} speed {speed} leap {leap to the enemy} slow {slow} other {stun}} · {share}",
});
const DEFENSE = msg({
  message: "defense against {kind, select, bullet {bullets} spirit {spirit} other {melee}}",
});
const SHARES = msg({ message: "{share} · usually {usual}" });
const TIMES = msg({ message: "{times}× more often" });

// Пометка под вариантом после ответа, 2–3 слова: чем он помогает или почему не подходит.
// Длинных пояснений под ответом нет — всё нужное на самих вариантах.
export const useOptionNote = (): ((
  spec: DrillSpec,
  option: DrillOption,
) => OptionNoteView | null) => {
  const { i18n, t } = useLingui();
  const percent = (share: number | null | undefined) => formatPercent(share ?? 0, i18n.locale);
  return (spec, option) => {
    const correct = spec.correct_ids.includes(option.id);
    switch (option.note) {
      case "dash":
      case "speed":
      case "leap":
      case "slow":
      case "stun":
        return {
          text: i18n._({ ...CHASE, values: { kind: option.note, share: percent(option.value) } }),
          tone: "good",
        };
      case "counter":
        return { text: t`works against him, but won't catch him`, tone: "muted" };
      case "not_chase":
        return { text: t`won't help catch him`, tone: "muted" };
      case "defense":
        return {
          text: i18n._({ ...DEFENSE, values: { kind: spec.damage_kind ?? "melee" } }),
          tone: "good",
        };
      case "other_defense":
        return { text: t`defense against other damage`, tone: "muted" };
      case "not_defense":
        return { text: t`not a defense`, tone: "muted" };
      case null:
        break;
    }
    if (option.mine) {
      return { text: t`your pick in the match`, tone: "bad" };
    }
    // Предметы против врага: у верных — доли против него и обычная (у старых задач — во
    // сколько раз чаще), у остальных — «как обычно».
    const againstEnemy =
      (spec.kind === "killer_counters" && spec.damage_kind == null) || spec.kind === "finish_him";
    if (!againstEnemy) {
      return null;
    }
    if (!correct) {
      return { text: t`as usual`, tone: "muted" };
    }
    if (option.pick_rate != null && option.usual_pick_rate != null) {
      return {
        text: i18n._({
          ...SHARES,
          values: { share: percent(option.pick_rate), usual: percent(option.usual_pick_rate) },
        }),
        tone: "good",
      };
    }
    const times = formatNumber(option.value ?? 1, i18n.locale, { maximumFractionDigits: 1 });
    return { text: i18n._({ ...TIMES, values: { times } }), tone: "good" };
  };
};
